import { applyChangeset, emptyDataset, type Changeset, type Dataset } from '@vero/domain';
import type { FileMeta, SnapshotMeta, StorageAdapter } from './adapter.ts';
import { StorageError } from './adapter.ts';
import { LATEST_VERSION } from './migrations.ts';

const clone = <T>(v: T): T => structuredClone(v);

/** In-memory adapter: reference implementation for tests and failure injection. */
export class MemoryAdapter implements StorageAdapter {
  readonly engine = 'memory';
  private ds: Dataset = emptyDataset();
  private meta = new Map<string, unknown>();
  private snaps = new Map<string, { meta: SnapshotMeta; content: string }>();
  /** failure injection for rollback tests: the next N commits/replaces throw after partial work */
  failNext = 0;

  async open() { const created = !this.meta.has('schemaVersion'); this.meta.set('schemaVersion', LATEST_VERSION); return { schemaVersion: LATEST_VERSION, created }; }
  async loadAll() { return clone(this.ds); }
  async commit(cs: Changeset) {
    if (this.failNext > 0) { this.failNext--; throw new StorageError('storage.failed', 'injected failure'); }
    this.ds = applyChangeset(this.ds, clone(cs));
  }
  async replaceAll(ds: Dataset) {
    if (this.failNext > 0) { this.failNext--; throw new StorageError('storage.failed', 'injected failure'); }
    this.ds = clone(ds);
  }
  async getMeta<T>(key: string) { return this.meta.get(key) as T | undefined; }
  async setMeta(key: string, value: unknown) { this.meta.set(key, clone(value)); }
  async saveSnapshot(meta: SnapshotMeta, content: string) { this.snaps.set(meta.id, { meta, content }); }
  async listSnapshots() { return [...this.snaps.values()].map((s) => s.meta).sort((a, b) => b.createdAt.localeCompare(a.createdAt)); }
  async readSnapshot(id: string) { return this.snaps.get(id)?.content; }
  async deleteSnapshot(id: string) { this.snaps.delete(id); }
  private files = new Map<string, { meta: FileMeta; content: Uint8Array }>();
  async putFile(meta: FileMeta, content: Uint8Array) { this.files.set(meta.id, { meta: clone(meta), content: content.slice() }); }
  async getFile(id: string) { const f = this.files.get(id); return f ? { meta: clone(f.meta), content: f.content.slice() } : undefined; }
  async deleteFile(id: string) { this.files.delete(id); }
  async listFiles() { return [...this.files.values()].map((f) => clone(f.meta)); }
  /** test helper: raw stored objects (to prove encryption at rest) */
  rawRows(table: keyof Dataset) { return clone(this.ds[table]) as unknown[]; }
  rawMeta(key: string) { return this.meta.get(key); }
  rawFile(id: string) { return this.files.get(id)?.content.slice(); }
  async close() {}
}
